import type { ReactNode } from "react";
import type { HelpCitation, HelpStats, HelpTicketKind, HelpTicketStatus } from "@backstory/protocol";
import { formatDateTime, formatRelative } from "../../lib/format";
import { AiOutput } from "../ai/components/common";
import "../ai/ai.css";
import "../insights/styles.css";

export const PAGE_SIZE = 30;

export const kindLabel: Record<HelpTicketKind, string> = { bug: "Bug", feature: "Feature", question: "Question" };

export function kindTone(kind: HelpTicketKind) {
  return kind === "bug" ? "danger" : kind === "feature" ? "info" : "neutral";
}

export function ticketTone(status: HelpTicketStatus) {
  if (status === "resolved" || status === "planned") return "ok";
  if (status === "waiting_on_us") return "warn";
  if (status === "declined") return "neutral";
  if (status === "open") return "info";
  return "neutral";
}

export const kindOptions = [
  { value: "", label: "All types" },
  { value: "bug", label: "Bug" },
  { value: "feature", label: "Feature" },
  { value: "question", label: "Question" },
];

export function KindBadge({ kind }: { kind: HelpTicketKind }) {
  return <span className={`badge ${kindTone(kind)} help-kind`}>{kindLabel[kind]}</span>;
}

/** Kind and title on one line, so a queue row stays one line tall. */
export function TicketTitle({ kind, title }: { kind: HelpTicketKind; title: string }) {
  return (
    <span className="help-title-line">
      <KindBadge kind={kind} />
      <strong title={title}>{title}</strong>
    </span>
  );
}

export function initialOf(name: string) {
  const c = name.trim().charAt(0);
  return c ? c.toUpperCase() : "?";
}

export function ThreadMessage({
  name,
  role,
  tone,
  at,
  badge,
  draft,
  children,
  citations,
  footer,
}: {
  name: string;
  role: string;
  tone: "customer" | "staff" | "ai";
  at: string;
  badge?: ReactNode;
  draft?: boolean;
  children: ReactNode;
  citations?: HelpCitation[];
  footer?: ReactNode;
}) {
  const inner = (
    <>
      <div className="help-msg-head">
        {tone !== "ai" && <span className={`help-avatar help-avatar-${tone}`} aria-hidden="true">{initialOf(name)}</span>}
        <div className="help-msg-who">
          <strong>{name}</strong>
          <span className="muted small">{role}</span>
          {badge}
        </div>
        <time className="muted small" dateTime={at} title={formatDateTime(at)}>{formatRelative(at)}</time>
      </div>
      <div className="help-msg-body">{children}</div>
      {citations && citations.length > 0 && (
        <div className="help-cites">
          <span className="muted small">Sources</span>
          <ul>
            {citations.map((c) => (
              <li key={c.source + c.id}>{c.url ? <a href={c.url} target="_blank" rel="noreferrer">{c.title}</a> : c.title}</li>
            ))}
          </ul>
        </div>
      )}
      {footer && <div className="help-msg-foot">{footer}</div>}
    </>
  );
  // A reply written by the model wears the same frame as every other AI answer
  // in the dashboard, so the provenance mark is the one people already know.
  if (tone === "ai") {
    return (
      <li className={`help-msg help-msg-ai${draft ? " help-msg-draft" : ""}`}>
        <AiOutput className="help-msg-ai-output">{inner}</AiOutput>
      </li>
    );
  }
  return <li className={`help-msg help-msg-${tone}`}>{inner}</li>;
}

export function formatMinutes(min: number) {
  if (min < 1) return "under a minute";
  if (min < 60) return `${Math.round(min)} min`;
  const h = min / 60;
  if (h < 48) return `${h < 10 ? h.toFixed(1).replace(/\.0$/, "") : Math.round(h)} h`;
  return `${Math.round(h / 24)} d`;
}

export interface HelpMetric {
  label: string;
  value: number | string | undefined | null;
  /** Status this tile stands for. Clicking it applies that status filter. */
  status?: string;
  footer?: ReactNode;
  tone?: "warn" | "ok";
}

/**
 * A row of help desk tiles. A tile that stands for a status is a button that
 * applies the filter and shows as pressed while that filter is on.
 */
export function HelpMetrics({ metrics, loading, activeStatus, onStatus, label }: {
  metrics: HelpMetric[];
  loading?: boolean;
  activeStatus?: string;
  onStatus?: (status: string) => void;
  label: string;
}) {
  return (
    <div className="viz-tiles help-metrics" role="group" aria-label={label} aria-busy={loading || undefined}>
      {metrics.map((m) => {
        const text = loading ? null : m.value === undefined || m.value === null ? "—" : typeof m.value === "number" ? m.value.toLocaleString() : m.value;
        const inner = (
          <>
            <span className="label">{m.label}</span>
            <span className="value">{loading ? <span className="skeleton" style={{ display: "inline-block", width: 48, height: "0.8em" }} /> : text}</span>
            {!loading && m.footer && <span className="muted small help-metric-foot">{m.footer}</span>}
          </>
        );
        const tone = m.tone && !loading && typeof m.value === "number" && m.value > 0 ? ` ${m.tone}` : "";
        if (m.status && onStatus) {
          const pressed = activeStatus === m.status;
          return (
            <button
              key={m.label}
              type="button"
              className={`card viz-tile help-metric help-metric-btn${tone}${pressed ? " pressed" : ""}`}
              aria-pressed={pressed}
              onClick={() => onStatus(pressed ? "all" : m.status!)}
              title={pressed ? "Show all requests" : `Show ${m.label.toLowerCase()} requests`}
            >
              {inner}
            </button>
          );
        }
        return (
          <div key={m.label} className={`card viz-tile help-metric${tone}`} role="group" aria-label={`${m.label}: ${text ?? "loading"}`}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

/** Tiles shared by the staff and customer pages, worded for whoever is looking. */
export function ticketMetrics(s: HelpStats | undefined, who: "staff" | "customer"): HelpMetric[] {
  const b = s?.byStatus;
  const oldest = s?.oldestWaitingOnUsAt;
  return [
    { label: "Open", value: b?.open, status: "open" },
    {
      label: who === "staff" ? "Waiting on us" : "Waiting on Backstory",
      value: b?.waiting_on_us,
      status: "waiting_on_us",
      tone: "warn",
      footer: oldest ? `Longest wait ${formatRelative(oldest).replace(/ ago$/, "")}` : undefined,
    },
    { label: who === "staff" ? "Waiting on customer" : "Waiting on you", value: b?.waiting_on_them, status: "waiting_on_them" },
    { label: "Planned", value: b?.planned, status: "planned" },
    { label: "Resolved", value: b?.resolved, status: "resolved", footer: s ? `${s.resolvedLast7d.toLocaleString()} in the last 7 days` : undefined },
    {
      label: "First reply",
      value: s ? (s.firstReplyMedianMinutes == null ? "—" : formatMinutes(s.firstReplyMedianMinutes)) : undefined,
      footer: "Typical, last 30 days",
    },
  ];
}

export function TicketSkeletonRows({ count, staff = true }: { count: number; staff?: boolean }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <tr key={`sk-${i}`} className="static help-skeleton" aria-hidden="true">
          <td className="help-col-request">
            <span className="help-title-line">
              <span className="skeleton" style={{ width: 56 }}>loading</span>
              <span className="skeleton" style={{ width: `${45 + ((i * 17) % 35)}%` }}>loading</span>
            </span>
          </td>
          {staff && <td className="help-col-customer"><span className="skeleton" style={{ width: 110 }}>loading</span></td>}
          <td className="help-col-status"><span className="skeleton" style={{ width: 72 }}>loading</span></td>
          <td className="help-col-updated"><span className="skeleton" style={{ width: 56 }}>loading</span></td>
        </tr>
      ))}
    </>
  );
}
